import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchUserCircles, isCircleVisible } from "@core/features/circle";
import { usePlatformStore } from "@platform/store";
import { useCurrentMeeting } from "@platform/hooks/useCurrentMeeting";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { hasJoinableMeeting } from "@platform/utils/hasJoinableMeeting";
import { getClosestMeetingToCurrentTime } from "@platform/utils/getClosestMeetingToCurrentTime";
import { getUserMeetingRoleFromMeeting } from "@platform/features/programPhases/utils";
import { SchedulingCohortMessageContainer } from "@platform/features/programScheduling";
import { CohortDashboardBanner } from "./CohortDashboardBanner";

export function CohortDashboardBannerContainer() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const programId = id ? Number(id) : undefined;
  const user = usePlatformStore((s) => s.user);

  const { data: circlesData } = useFetchUserCircles({ user });
  const visibleCircle = useMemo(
    () => (circlesData?.circles ?? []).find(isCircleVisible) ?? null,
    [circlesData?.circles]
  );

  const { currentMeeting } = useCurrentMeeting();
  const hasMeeting = hasJoinableMeeting(currentMeeting);

  const { meetings } = useUserMeetings();
  const role = useMemo(() => {
    if (!visibleCircle || !user) return null;
    const circleMeetings = meetings.filter(
      (m) => m.circle === visibleCircle.id
    );
    if (circleMeetings.length === 0) return null;
    const closestMeeting = getClosestMeetingToCurrentTime({
      meetings: circleMeetings,
    });
    if (!closestMeeting) return null;
    return getUserMeetingRoleFromMeeting(closestMeeting, user.id);
  }, [meetings, visibleCircle, user]);

  if (!visibleCircle || !programId) {
    return null;
  }

  return (
    <CohortDashboardBanner
      message={<SchedulingCohortMessageContainer />}
      hasMeeting={hasMeeting}
      role={role}
      onScheduleClick={() => navigate(`/program/${programId}/schedule`)}
      onCohortClick={() => navigate(`/program/${programId}/cohort`)}
      onJoinClick={() => navigate("/video")}
    />
  );
}

export default CohortDashboardBannerContainer;
